import { useState, useEffect, useCallback } from 'react';
import { Container, Table, Badge, Row, Col, Spinner, Form, FormGroup, FormLabel, Card, InputGroup, FormControl, Pagination, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import SearchIcon from "../../../images/search-icon.svg"
import Icon1 from "../../../images/management/users-icon.svg";
import Icon2 from "../../../images/management/activity-icon.svg";
import Icon3 from "../../../images/management/inactive-icon.svg";
import DateRangeSelector from '../../DateRangeSelector';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../../utils.ts';
import ShortMain from "../../../images/short-icon-main.svg"
import ShortUp from "../../../images/short-icon-top.svg"
import ShortDown from "../../../images/short-icon-bottom.svg"
import axiosInstance from '../../../AxiosInstance.tsx';
import { useApiLoader } from "../../../hooks/useApiLoader.tsx";
import "./Users.scss"
import { formatPhoneNumber } from '../../../utils/phoneFormatter';
import { HiOutlineDownload } from 'react-icons/hi';
import ExportModal from '../../ExportModal';
import { exportUsers, downloadFile, getExportFilename } from '../../../utils/exportUtils';

interface User {
  _id: string;
  userId: string;
  customerName: string;
  email: string;
  phone: string;
  subscription: string;
  registrationDate: string;
  lastActive: string;
  status: string;
}

interface PaginationInfo {
  totalCount: number;
  totalPages: number;
  currentPage: number;
}

interface SortConfig {
  sortBy: string;
  sortOrder: 'asc' | 'desc' | '';
}

interface DateRange {
  startDate: string;
  endDate: string;
}

const Users = () => {
  const { withLoader } = useApiLoader();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationInfo>({
    totalCount: 0,
    totalPages: 0,
    currentPage: 1
  });
  const [filterBy, setFilterBy] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    sortBy: '',
    sortOrder: ''
  });
  const [usersStats, setUsersStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    closed: 0
  });

  // Enhanced date range state with proper formatting
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: '',
    endDate: ''
  });
  const [selectedCardFilter, setSelectedCardFilter] = useState('');
  const [showExportModal, setShowExportModal] = useState(false);

  const navigate = useNavigate();
  const ITEMS_PER_PAGE = 10;

  // Format date for API (YYYY-MM-DD format expected by backend)
  const formatDateForAPI = (dateString: string): string => {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';

      // Format as YYYY-MM-DD for backend
      return date.toISOString().split('T')[0];
    } catch (error) {
      return '';
    }
  };

  const formatDate = (dateString: any) => {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return '';
      }

      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();

      return `${month}/${day}/${year}`;
    } catch (error) {
      return '';
    }
  };

  // Capitalize text helper
  const capitalizeText = (text: string) => {
    if (!text) return 'N/A';
    // Capitalize first letter of each word
    return text
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Phone number formatting function is now imported from utils


  const fetchUserCounts = async () => {
    try {
      setStatsLoading(true);
      const authToken = localStorage.getItem('authToken');

      if (!authToken) {
        toast.error('Authentication required');
        return;
      }

      const response = await withLoader(
        () => axiosInstance.get(API_ENDPOINTS.GET_USER_COUNTS, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          }
        }),
        'Loading user statistics...'
      );

      if (response.data.status === 'Success') {
        const userCounts = response.data.userCounts || {
          totalUsers: 0,
          activeUsers: 0,
          inactiveUsers: 0,
          suspendedUsers: 0
        };

        setUsersStats({
          total: userCounts.totalUsers,
          active: userCounts.activeUsers,
          inactive: userCounts.inactiveUsers,
          closed: userCounts.suspendedUsers
        });
      }
    } catch (error: any) {
      console.error('Error fetching user counts:', error);
    } finally {
      setStatsLoading(false);
    }
  };


  const fetchUsers = useCallback(async (
    page = 1,
    search = '',
    filter = '',
    limit = ITEMS_PER_PAGE,
    sortBy = '',
    sortOrder = '',
    startDate = '',
    endDate = '',
    cardFilter = ''
  ) => {
    try {
      setLoading(true);
      const authToken = localStorage.getItem('authToken');

      if (!authToken) {
        toast.error('Authentication required');
        return;
      }

      // Build dynamic parameters object with proper date formatting
      const params: any = {
        page,
        limit,
        search: search.trim()
      };

      // Add filter if selected
      if (filter && filter !== '') {
        params.filter = filter;
      }

      // Add card filter (when user clicks on stats cards)
      if (cardFilter && cardFilter !== '') {
        params.status = cardFilter;
      }

      // Add sorting parameters
      if (sortBy) {
        params.sortBy = sortBy;
        params.sortOrder = sortOrder;
      }

      // Add date range parameters with proper formatting
      if (startDate) {
        const formattedStartDate = formatDateForAPI(startDate);
        if (formattedStartDate) {
          params.startDate = formattedStartDate;
        }
      }
      if (endDate) {
        const formattedEndDate = formatDateForAPI(endDate);
        if (formattedEndDate) {
          params.endDate = formattedEndDate;
        }
      }

      const response = await withLoader(
        () => axiosInstance.get(API_ENDPOINTS.GET_USERS, {
          params,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          }
        }),
        'Loading users...'
      );

      if (response.data.status === 'Success') {
        const userData = response.data.data || [];
        const paginationData = response.data.pagination || {
          totalCount: 0,
          totalPages: 0,
          currentPage: 1
        };

        setUsers(userData);
        setPagination(paginationData);
        setCurrentPage(paginationData.currentPage);

      } else {
        // toast.error(response.data.message || 'Failed to fetch users');
        setUsers([]);
        setPagination({
          totalCount: 0,
          totalPages: 0,
          currentPage: 1
        });
      }
    } catch (error: any) {
      // toast.error(error.response?.data?.message || 'Failed to fetch users');
      setUsers([]);
      setPagination({
        totalCount: 0,
        totalPages: 0,
        currentPage: 1
      });
    } finally {
      setLoading(false);
    }
  }, [withLoader]); // Add dependencies for useCallback

  // Enhanced date range change handler with validation
  const handleDateRangeChange = useCallback((dateRangeString: string, isCustom?: boolean) => {
    // Parse the date range string "MM/DD/YYYY - MM/DD/YYYY"
    if (!dateRangeString || dateRangeString.trim() === '') {
      // Clear the filter
      setDateRange({ startDate: '', endDate: '' });
      setCurrentPage(1);
      setLoading(true);

      fetchUsers(
        1,
        searchTerm,
        filterBy,
        ITEMS_PER_PAGE,
        sortConfig.sortBy,
        sortConfig.sortOrder,
        '',
        '',
        selectedCardFilter
      );
      return;
    }

    // Split the date range string
    const dates = dateRangeString.split(' - ');
    if (dates.length === 2) {
      const startDateStr = dates[0].trim();
      const endDateStr = dates[1].trim();

      // Convert MM/DD/YYYY to YYYY-MM-DD without timezone conversion
      const parseDate = (dateStr: string): string => {
        const [month, day, year] = dateStr.split('/');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      };

      const formattedStartDate = parseDate(startDateStr);
      const formattedEndDate = parseDate(endDateStr);

      // Validate dates
      if (formattedStartDate > formattedEndDate) {
        toast.error('Start date cannot be after end date');
        return;
      }

      setDateRange({ startDate: formattedStartDate, endDate: formattedEndDate });
      setCurrentPage(1);

      // Show loading for date filter
      setLoading(true);

      fetchUsers(
        1,
        searchTerm,
        filterBy,
        ITEMS_PER_PAGE,
        sortConfig.sortBy,
        sortConfig.sortOrder,
        formattedStartDate,
        formattedEndDate,
        selectedCardFilter
      );
    }
  }, [searchTerm, filterBy, sortConfig.sortBy, sortConfig.sortOrder, selectedCardFilter, fetchUsers]);

  // Handle card clicks for filtering with date range clearing
  const handleCardClick = (cardType: string) => {
    let statusFilter = '';

    switch (cardType) {
      case 'active':
        statusFilter = 'Active';
        break;
      case 'inactive':
        statusFilter = 'Inactive';
        break;
      case 'closed':
        statusFilter = 'Suspended';
        break;
      case 'total':
      default:
        statusFilter = '';
        break;
    }

    setSelectedCardFilter(statusFilter);
    setCurrentPage(1);

    // Clear date range when clicking on metric cards
    setDateRange({ startDate: '', endDate: '' });

    fetchUsers(1, searchTerm, filterBy, ITEMS_PER_PAGE, sortConfig.sortBy, sortConfig.sortOrder, '', '', statusFilter);
  };

  // Enhanced sorting logic with three states - maintains current page
  const handleSort = (field: string) => {
    let newSortOrder: 'asc' | 'desc' = 'asc';

    if (sortConfig.sortBy === field) {
      if (sortConfig.sortOrder === 'asc') {
        newSortOrder = 'desc';
      } else if (sortConfig.sortOrder === 'desc') {
        // Reset sorting - no sort, maintain current page
        setSortConfig({ sortBy: '', sortOrder: '' });
        fetchUsers(currentPage, searchTerm, filterBy, ITEMS_PER_PAGE, '', '', dateRange.startDate, dateRange.endDate, selectedCardFilter);
        return;
      }
    }

    setSortConfig({ sortBy: field, sortOrder: newSortOrder });
    // Maintain current page when sorting to preserve user's position
    // The fetchUsers function will handle cases where currentPage > totalPages
    fetchUsers(currentPage, searchTerm, filterBy, ITEMS_PER_PAGE, field, newSortOrder, dateRange.startDate, dateRange.endDate, selectedCardFilter);
  };

  // Get sort icon for sortable columns
  const getSortIcon = (field: string) => {
    if (sortConfig.sortBy !== field) {
      return (
        <span className='short-icon'>
          <img src={ShortMain} alt='' />
        </span>
      );
    }

    if (sortConfig.sortOrder === 'asc') {
      return (
        <span className='short-icon'>
          <img src={ShortUp} alt='' />
        </span>
      );
    } else {
      return (
        <span className='short-icon'>
          <img src={ShortDown} alt='' />
        </span>
      );
    }
  };

  const stripPhoneFormatting = (phoneNumber) => {
    if (!phoneNumber) return '';
    // Remove all non-digit characters
    return phoneNumber.toString().replace(/\D/g, '');
  };

  // Handle pagination with boundary checks
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.totalPages && page !== currentPage) {
      setCurrentPage(page);
      fetchUsers(page, searchTerm, filterBy, ITEMS_PER_PAGE, sortConfig.sortBy, sortConfig.sortOrder, dateRange.startDate, dateRange.endDate, selectedCardFilter);
    }
  };

  const handleFilterChange = (value: string) => {
    setFilterBy(value);
    setSelectedCardFilter(value);
    setCurrentPage(1);

    // Use the same value for both filterBy and cardFilter
    fetchUsers(
      1,
      searchTerm,
      value,
      ITEMS_PER_PAGE,
      sortConfig.sortBy,
      sortConfig.sortOrder,
      dateRange.startDate,
      dateRange.endDate,
      value
    );
  };

  // Handle search with debouncing - only for searchTerm changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      
      // Strip formatting from search value if it looks like a phone number
      let searchValue = searchTerm;
      const digitsOnly = stripPhoneFormatting(searchTerm);

      if (digitsOnly.length === 10 && /^\d+$/.test(digitsOnly)) {
        searchValue = digitsOnly;
      }

      fetchUsers(1, searchValue, filterBy, ITEMS_PER_PAGE, sortConfig.sortBy, sortConfig.sortOrder, dateRange.startDate, dateRange.endDate, selectedCardFilter);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initial data load
  useEffect(() => {
    // Fetch both users and user counts on initial load
    fetchUsers(1, '', '', ITEMS_PER_PAGE, '', '', '', '', '');
    fetchUserCounts();
  }, []);

  // Handle view user details
  const handleView = (user: User) => {
    navigate(`/billing-details/${user._id}`, { state: { customer: user } });
  };

  // Handle edit user
  const handleEdit = (user: User) => {
    toast.info(`Editing ${user.customerName}'s information`);
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    return (
      <Badge
        color={status === 'Active' ? 'success' : 'danger'}
        pill
        className={status === 'Active' ? 'status-active' : 'status-inactive'}
      >
        {status}
      </Badge>
    );
  };

  // Handle export
  const handleExport = () => {
    setShowExportModal(true);
  };

  const handleExportExcel = async () => {
    try {
      toast.info('Preparing Excel export...');

      // Call export API with date range if available
      const blob = await exportUsers('excel', dateRange.startDate, dateRange.endDate);

      // Generate filename
      const filename = getExportFilename('Users', 'excel', dateRange.startDate, dateRange.endDate);

      // Download the file
      downloadFile(blob, filename);

      toast.success('Excel file downloaded successfully!');
    } catch (error: any) {
      console.error('Export Excel error:', error);

      // Handle specific error messages
      if (error.response?.status === 404) {
        toast.error('No users found in the selected date range.');
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.message || 'Invalid export parameters.');
      } else {
        toast.error('Failed to export Excel file. Please try again.');
      }
    }
  };

  const handleExportPdf = async () => {
    try {
      toast.info('Preparing PDF export...');

      // Call export API with date range if available
      const blob = await exportUsers('pdf', dateRange.startDate, dateRange.endDate);

      // Generate filename
      const filename = getExportFilename('Users', 'pdf', dateRange.startDate, dateRange.endDate);

      // Download the file
      downloadFile(blob, filename);

      toast.success('PDF file downloaded successfully!');
    } catch (error: any) {
      console.error('Export PDF error:', error);

      // Handle specific error messages
      if (error.response?.status === 404) {
        toast.error('No users found in the selected date range.');
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.message || 'Invalid export parameters.');
      } else {
        toast.error('Failed to export PDF file. Please try again.');
      }
    }
  };

  // Stats cards configuration with click handlers
  const data = [
    {
      icon: Icon1,
      value: usersStats.total,
      label: "Total Customers",
      colorClass: "blue-text",
      onClick: () => handleCardClick('total'),
      isActive: selectedCardFilter === ''
    },
    {
      icon: Icon2,
      value: usersStats.active,
      label: "Active Customers",
      colorClass: "green-text",
      onClick: () => handleCardClick('active'),
      isActive: selectedCardFilter === 'Active'
    },
    {
      icon: Icon3,
      value: usersStats.inactive,
      label: "Inactive Customers",
      colorClass: "red-text",
      onClick: () => handleCardClick('inactive'),
      isActive: selectedCardFilter === 'Inactive'
    },
  ];

  return (
    <>
      {/* Bootstrap Icons CSS */}
      <link
        href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.0/font/bootstrap-icons.css"
        rel="stylesheet"
      />

      <section className='main-body'>
        <Container fluid className="p-0">
          <Card.Body>


            {/* Statistics Cards with enhanced click functionality */}
            <div className="icon-grid-sec">
              <ul className='icon-grid mb-4'>
                <div className='row-3 m-0'>
                  {data.map((item, idx) => (
                    <li
                      className={`icon-block ${item.isActive ? 'active-card' : ''}`}
                      key={idx}
                      onClick={item.onClick}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="icon-block-in d-flex justify-content-between align-items-center">
                        <figure><img src={item.icon} alt="" /></figure>
                        <div className="icon-grid-cont">
                          <h2 className={item.colorClass}>
                            {statsLoading ? <Spinner size="sm" /> : item.value}
                          </h2>
                          <p>{item.label}</p>
                        </div>
                      </div>
                    </li>
                  ))}
                </div>
                <div className="clearfix"></div>
              </ul>
            </div>

            {/* Header with Enhanced Calendar */}


            {/* Main Table Section */}
            <div className='main-table'>
              {/* Filter and Search Row */}
              <div className="mb-3 main-table-filter">
                <div className="row g-2 align-items-stretch">
                  {/* Search Field */}
                  <div className='col-lg-3 col-md-6 col-12'>
                    <div className='main-table-search h-100'>
                      <FormGroup className='form-group search-box mb-0 h-100'>
                        <InputGroup className="h-100">
                          <FormControl
                            type="text"
                            placeholder="Search users..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="h-100"
                          />
                          <InputGroup.Text>
                            <img src={SearchIcon} alt='' />
                          </InputGroup.Text>
                        </InputGroup>
                      </FormGroup>
                    </div>
                  </div>

                  {/* Filter Dropdown */}
                  <div className='col-lg-2 col-md-6 col-12'>
                    <div className='select-field h-100'>
                      <FormGroup className='form-group mb-0 h-100'>
                        <Form.Select
                          value={filterBy}
                          onChange={(e) => handleFilterChange(e.target.value)}
                          className="h-100"
                        >
                          <option value="">Filter by</option>
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                        </Form.Select>
                      </FormGroup>
                    </div>
                  </div>

                  {/* Spacer to push date range and export to the right */}
                  <div className='col-lg-3 d-none d-lg-block'></div>

                  {/* Date Range Picker */}
                  <div className='col-lg-3 col-md-9 col-12'>
                    <div className='calender-block h-100'>
                      <DateRangeSelector
                        onDateRangeChange={handleDateRangeChange}
                      />
                    </div>
                  </div>

                  {/* Export Button */}
                  <div className='col-lg-1 col-md-3 col-12'>
                    <Button
                      variant="primary"
                      onClick={handleExport}
                      className="d-flex align-items-center justify-content-center gap-2 h-100 w-100"
                    >
                      <HiOutlineDownload size={18} />
                      <span>Export</span>
                    </Button>
                  </div>
                </div>
              </div>

              {/* Users Table */}
              <div className="table-responsive mt-3">
                <Table hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th
                        onClick={() => handleSort('userId')}
                        style={{ cursor: 'pointer' }}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                          Customer ID
                          {getSortIcon('userId')}
                        </span>
                      </th>
                      <th style={{ fontWeight: '600', fontSize: '0.875rem' }}>Name</th>
                      <th style={{ fontWeight: '600', fontSize: '0.875rem' }}>Email</th>
                      <th style={{ fontWeight: '600', fontSize: '0.875rem' }}>Phone</th>
                                            <th style={{ fontWeight: '600', fontSize: '0.875rem' }}>Registration Date</th>
                      <th
                        onClick={() => handleSort('subscription')}
                        style={{ cursor: 'pointer' }}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                          Subscription
                          {getSortIcon('subscription')}
                        </span>
                      </th>

                      <th
                        style={{
                          fontWeight: '600',
                          fontSize: '0.875rem',
                          cursor: 'pointer',
                          userSelect: 'none',
                          verticalAlign: 'middle'
                        }}
                        onClick={() => handleSort('lastActive')}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                          Last Activity
                          {getSortIcon('lastActive')}
                        </span>
                      </th>
                      <th style={{ fontWeight: '600', fontSize: '0.875rem' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={8} className="text-center py-4">
                          <Spinner color="primary" />
                          <p className="mt-2 mb-0">Loading users...</p>
                        </td>
                      </tr>
                    ) : users.length > 0 ? (
                      users.map((user) => (
                        <tr 
                          key={user._id}
                          onClick={() => handleView(user)}
                          style={{ 
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '';
                          }}
                        >
                          <td>#{user.userId}</td>
                          <td>{capitalizeText(user.customerName)}</td>
                          <td>{user.email}</td>
                          <td>{formatPhoneNumber(user.phone || 'N/A')}</td>
                          <td>{formatDate(user.registrationDate)}</td>
                          <td>{capitalizeText(user.subscription)}</td>
                          <td>{formatDate(user.lastActive || 'N/A')}</td>
                          <td>{getStatusBadge(user.status)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={8} className="text-center py-4">
                          <p className="text-muted mb-0">
                            {(dateRange.startDate || dateRange.endDate)
                              ? 'No users found in the selected date range.'
                              : searchTerm
                                ? 'No users found matching your search criteria.'
                                : 'No users found.'
                            }
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {!loading && users.length > 0 && pagination.totalPages > 1 && (
                <div className="pagination-wrapper mt-3">
                  <div className="pagination-info-row">
                    <div className="text-muted">
                      Showing {((pagination.currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(pagination.currentPage * ITEMS_PER_PAGE, pagination.totalCount)} of {pagination.totalCount} entries
                      {(dateRange.startDate || dateRange.endDate) && (
                        <span className="ms-2">(filtered by date range)</span>
                      )}
                    </div>
                  </div>
                  <div className="pagination-controls-row">
                    <Pagination>
                    {/* Previous Button */}
                    <Pagination.Prev
                      disabled={pagination.currentPage === 1 || loading}
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                    />

                    {/* First Page */}
                    {pagination.currentPage > 3 && (
                      <>
                        <Pagination.Item
                          onClick={() => handlePageChange(1)}
                          disabled={loading}
                        >
                          1
                        </Pagination.Item>
                        {pagination.currentPage > 4 && (
                          <Pagination.Ellipsis disabled />
                        )}
                      </>
                    )}

                    {/* Page Numbers Around Current Page */}
                    {Array.from({ length: pagination.totalPages }, (_, index) => index + 1)
                      .filter(page =>
                        page >= Math.max(1, pagination.currentPage - 2) &&
                        page <= Math.min(pagination.totalPages, pagination.currentPage + 2)
                      )
                      .map(page => (
                        <Pagination.Item
                          key={page}
                          active={page === pagination.currentPage}
                          onClick={() => handlePageChange(page)}
                          disabled={loading}
                        >
                          {page}
                        </Pagination.Item>
                      ))
                    }

                    {/* Last Page */}
                    {pagination.currentPage < pagination.totalPages - 2 && (
                      <>
                        {pagination.currentPage < pagination.totalPages - 3 && (
                          <Pagination.Ellipsis disabled />
                        )}
                        <Pagination.Item
                          onClick={() => handlePageChange(pagination.totalPages)}
                          disabled={loading}
                        >
                          {pagination.totalPages}
                        </Pagination.Item>
                      </>
                    )}

                    {/* Next Button */}
                    <Pagination.Next
                      disabled={pagination.currentPage === pagination.totalPages || loading}
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                    />
                  </Pagination>
                  </div>
                </div>
              )}
            </div>
          </Card.Body>
        </Container>
      </section>

      {/* Export Modal */}
      <ExportModal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        onExportExcel={handleExportExcel}
        onExportPdf={handleExportPdf}
        title="Export Users Data"
      />
    </>
  );
};

export default Users;